import { withEquipmentType } from '@/app/considerations/appliesToPublishCheck';
import { useAppliesToGuard } from '@/db/useAppliesToGuard';
import { useBusyAction } from '@/db/useBusyAction';
import logger from '@/util/logger';
import { EquipmentTypeDao } from '@rock/db-admin';
import type { EquipmentTypeId } from '@rock/types';
import { toast } from 'sonner';

const log = logger('usePublishEquipmentType');
const equipmentTypeDao = new EquipmentTypeDao();

/**
 * Draft actions for one equipment type: promote the draft to live (everyone sees it) or discard it.
 * There's no separate preview step — admins live-preview the draft on-device via preview mode.
 */
export function usePublishEquipmentType(typeId: EquipmentTypeId) {
    const { busy, run } = useBusyAction(log, { typeId });
    const guard = useAppliesToGuard();

    return {
        busy,
        publish: () =>
            run('Publish', async () => {
                const draft = await equipmentTypeDao.getEquipmentTypeDraft(typeId);
                if (draft) await guard(`Publishing ${typeId}`, (catalog) => withEquipmentType(catalog, typeId, draft));
                await equipmentTypeDao.publishEquipmentType(typeId);
                toast.success('Published');
                log.info('Published equipment type', { typeId });
            }),
        discardDraft: () =>
            run('Discard Draft', async () => {
                await equipmentTypeDao.discardEquipmentTypeDraft(typeId);
                toast.success('Discarded draft changes');
                log.info('Discarded equipment type draft', { typeId });
            }),
    };
}
